import { useState } from 'react'
import { BOX_TRUCK_DOCK_HEIGHT_OPTIONS, BOX_TRUCK_EQUIPMENT_CAPABILITIES, EquipmentTypes, formatBoxTruckDockHeight, type Equipment } from '@numeo/types-core'
import type { TruckDraft } from '../utils/truckFromVerification'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Autocomplete, Select, TextField } from 'components/ds2'
import { BasicInformation } from 'components/shared/truck-forms/BasicInformation'
import { asOfferedSpelling, BoxTruckVehicleDetails, optionsIncludingStored } from 'pages/dashboard/pages/profiles/pages/assets/BoxTruckVehicleDetails'
import { BOX_TRUCK_EQUIPMENT_FILTER_OPTIONS } from '../utils/format'

interface ClaimDriverTruckFieldsProps {
    truck: TruckDraft
    onUpdate: (field: string, value: string | number | null | boolean | Equipment | string[]) => void
}

/**
 * The vehicle half of "Add my driver": six fields, with the rest of the Assets truck form one click away.
 *
 * Six, because those are the ones an operator has in front of them while they are looking at a driver they
 * are deciding to take on — and asking for the whole vehicle form at that moment is how this ends up skipped.
 * Everything else the form collects is real and worth having, so it is offered rather than removed: the SAME
 * components Assets uses, so a vehicle created here and one created there cannot come out differently shaped.
 *
 * The last two are what a load is matched against. Every other field here arrives prefilled from what the
 * driver submitted; the fitted equipment and the dock answer are asked nowhere else, so a vehicle created
 * without them starts blank and stays blank — and a load that wants a lift gate cannot tell this truck from
 * one that has none.
 */
export const ClaimDriverTruckFields = ({ truck, onUpdate }: ClaimDriverTruckFieldsProps) => {
    const [expanded, setExpanded] = useState(false)

    /** Vehicle type lives in two places — the DAT-style `equipment` list and the Box Truck profile — and a
     *  save that set only one is what left vehicles typed on one screen and blank on the other. */
    const setVehicleType = (vehicleType: string) => {
        onUpdate('equipment', { type: EquipmentTypes.TYPES, values: vehicleType ? [vehicleType] : [] } as Equipment)
        onUpdate('boxTruck.vehicleType', vehicleType)
    }

    return (
        <div className="mt-4 border-t border-border pt-4">
            <p className="text-sm font-semibold text-foreground">Their truck</p>
            <p className="mt-1 text-xs text-foreground-muted">
                Created as an asset and assigned to this driver. Prefilled from what they submitted — leave it empty to add the driver without a vehicle.
            </p>

            {expanded ? (
                <div className="mt-3">
                    {/* No `formRef`: nothing validates through it here. The Assets schema demands a plate,
                        and driver registration never supplies one — running it would refuse every claim. */}
                    <BasicInformation truckData={truck} onUpdate={onUpdate} boxTruckMode onBoxTruckEquipmentChange={setVehicleType} />
                    <BoxTruckVehicleDetails
                        truckData={truck}
                        showIdentity={false}
                        showAssignments={false}
                        onUpdate={onUpdate}
                        onVehicleTypeChange={setVehicleType}
                    />
                </div>
            ) : (
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <TextField variant="secondary" label="VIN" value={truck.vin || ''} onChange={(event) => onUpdate('vin', event.target.value)} />
                    <TextField
                        variant="secondary"
                        label="Truck Name"
                        value={truck.mainInfo || ''}
                        onChange={(event) => onUpdate('mainInfo', event.target.value)}
                    />
                    <TextField
                        variant="secondary"
                        label="Truck Plate Number"
                        value={truck.truckId || ''}
                        onChange={(event) => onUpdate('truckId', event.target.value)}
                    />
                    <Select
                        variant="secondary"
                        label="Equipment"
                        value={truck.boxTruck?.vehicleType || ''}
                        options={[{ value: '', label: 'Select equipment' }, ...BOX_TRUCK_EQUIPMENT_FILTER_OPTIONS.map((value) => ({ value, label: value }))]}
                        onChange={setVehicleType}
                    />
                    <div className="sm:col-span-2">
                        <Autocomplete
                            variant="secondary"
                            label="Equipment capabilities"
                            placeholder="Lift gate, pallet jack, straps…"
                            multiple
                            options={optionsIncludingStored(BOX_TRUCK_EQUIPMENT_CAPABILITIES, truck.boxTruck?.equipmentCapabilities).map((value) => ({
                                value,
                                label: value,
                            }))}
                            value={(truck.boxTruck?.equipmentCapabilities ?? []).map((value) => asOfferedSpelling(BOX_TRUCK_EQUIPMENT_CAPABILITIES, value))}
                            onChange={(values) => onUpdate('boxTruck.equipmentCapabilities', values)}
                        />
                    </div>
                    <Select
                        variant="secondary"
                        label="Dock Height / Ramps"
                        value={asOfferedSpelling(BOX_TRUCK_DOCK_HEIGHT_OPTIONS, truck.boxTruck?.dockHeightRamps)}
                        options={[
                            { value: '', label: 'Select dock height / ramps' },
                            ...optionsIncludingStored(BOX_TRUCK_DOCK_HEIGHT_OPTIONS, truck.boxTruck?.dockHeightRamps).map((value) => ({
                                value,
                                label: formatBoxTruckDockHeight(value) ?? value,
                            })),
                        ]}
                        onChange={(value) => onUpdate('boxTruck.dockHeightRamps', value)}
                    />
                </div>
            )}

            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
            >
                {expanded ? <ChevronUp size={14} strokeWidth={2} /> : <ChevronDown size={14} strokeWidth={2} />}
                {expanded ? 'Show less' : 'Add more info'}
            </button>
        </div>
    )
}
